import type { CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Locale } from '../../app/locale/resolveLocale';
import { allPersonalityTypes, findPersonalityType, regularTypes, type PersonalityType } from '../../data/taxonomy';
import { visualAssets } from '../../data/visualAssets';

function prefixFor(locale: Locale) {
  return locale === 'zh-CN' ? 'zh' : 'en';
}

function typeName(type: PersonalityType, locale: Locale) {
  return locale === 'zh-CN' ? type.zhShare : type.enShare;
}

function typeDetail(type: PersonalityType, locale: Locale) {
  return locale === 'zh-CN' ? type.zhDetail : type.enDetail;
}

function typeSummary(type: PersonalityType, locale: Locale) {
  if ('core' in type) return locale === 'zh-CN' ? type.core : `${type.enDetail} pattern shown through social reaction choices.`;
  return locale === 'zh-CN'
    ? `${type.zhDetail}，由重复行为信号触发的隐藏人格。`
    : `${type.enDetail}, triggered by repeated behavior signals.`;
}

function characterAlt(name: string, locale: Locale) {
  return locale === 'zh-CN' ? `${name}完整形象` : `${name} full character`;
}

const atlasCopy: Record<Locale, {
  brand: string;
  title: string;
  intro: string;
  regular: string;
  hidden: string;
  start: string;
  how: string;
  openType: (name: string) => string;
  back: string;
}> = {
  'zh-CN': {
    brand: '社交事故局',
    title: '社交事故人格图鉴',
    intro: '浏览常规人格和隐藏人格，看看不同社交反应会落在哪种事故档案里。',
    regular: '常规人格',
    hidden: '隐藏人格',
    start: '开始测试',
    how: '怎么玩？',
    openType: (name) => `查看${name}`,
    back: '返回图鉴'
  },
  en: {
    brand: 'Social Reflex',
    title: 'Social Mishap Type Atlas',
    intro: 'Browse regular and hidden types, then see how different social reactions land in a Social Mishap Report.',
    regular: 'Regular type',
    hidden: 'Hidden type',
    start: 'Start test',
    how: 'How it works',
    openType: (name) => `Open ${name}`,
    back: 'Back to atlas'
  }
};

export function TypeAtlasPage({ locale }: { locale: Locale }) {
  const copy = atlasCopy[locale];
  const prefix = prefixFor(locale);

  return (
    <main className="type-atlas-page">
      <section className="type-atlas-hero">
        <p className="landing-kicker">{copy.brand}</p>
        <h1>{copy.title}</h1>
        <p>{copy.intro}</p>
        <div className="landing-actions">
          <Link className="primary-link" to="/play">{copy.start}</Link>
          <Link className="secondary-link" to={`/${prefix}/how-it-works/`}>{copy.how}</Link>
        </div>
      </section>
      <section className="type-card-grid" aria-label={copy.title}>
        {allPersonalityTypes.map((type) => {
          const isHidden = !('core' in type);
          const asset = visualAssets[type.typeId];
          const name = typeName(type, locale);
          const detail = typeDetail(type, locale);
          const summary = typeSummary(type, locale);

          return (
            <article
              className="type-card"
              key={type.typeId}
              style={{ '--accent-primary': asset.primary, '--accent-secondary': asset.secondary } as CSSProperties}
            >
              <div className="type-card-topline">
                <img src={asset.hero} alt={characterAlt(name, locale)} />
                <span>{isHidden ? copy.hidden : copy.regular}</span>
              </div>
              <div className="type-card-copy">
                <h2>{name}</h2>
                <strong>{detail}</strong>
                <p>{summary}</p>
              </div>
              <Link className="type-card-link" to={`/${prefix}/types/${type.typeId}/`}>
                {copy.openType(name)}
              </Link>
            </article>
          );
        })}
      </section>
    </main>
  );
}

export function TypeDetailPage({ locale }: { locale: Locale }) {
  const params = useParams();
  const type = findPersonalityType(params.typeId ?? '') ?? regularTypes[0];
  const copy = atlasCopy[locale];
  const prefix = prefixFor(locale);
  const asset = visualAssets[type.typeId];

  return (
    <main className="type-detail-page" style={{ '--accent-primary': asset.primary, '--accent-secondary': asset.secondary } as CSSProperties}>
      <section className="type-detail-shell">
        <div className="type-detail-art">
          <img src={asset.hero} alt="" />
        </div>
        <div className="type-detail-copy">
          <p className="landing-kicker">{copy.brand}</p>
          <h1>{typeName(type, locale)}</h1>
          <strong>{typeDetail(type, locale)}</strong>
          <p>{typeSummary(type, locale)}</p>
          <div className="landing-actions">
            <Link className="primary-link" to="/play">{copy.start}</Link>
            <Link className="secondary-link" to={`/${prefix}/types/`}>{copy.back}</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
